import React, { useState, useEffect } from 'react';

const RepeatOffendersList = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRepeatOffenders();
  }, [interval]);

  const fetchRepeatOffenders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_safety/repeat_offenders_top10/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch repeat offenders data');
      }

      const result = await response.json();
      
      // Transform the data
      const transformedData = result.map((item, index) => ({
        rank: index + 1,
        userId: item[0] || 'Unknown',
        incidentCount: item[1] || 0,
        uniqueChats: item[2] || 0,
        firstSeen: item[3] ? formatDate(item[3]) : 'N/A',
        lastSeen: item[4] ? formatDate(item[4]) : 'N/A',
      }));

      setData(transformedData);
    } catch (err) {
      console.error('Error fetching repeat offenders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (count) => {
    if (count >= 10) return '#e53e3e'; // Red - Critical
    if (count >= 5) return '#f6ad55'; // Orange - High
    if (count >= 3) return '#feca57'; // Yellow - Medium
    return '#48bb78'; // Green - Low
  };

  const getSeverityBadge = (count) => {
    if (count >= 10) return 'Critical';
    if (count >= 5) return 'High';
    if (count >= 3) return 'Medium';
    return 'Low';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
        gap: '12px',
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e2e8f0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <span>Loading repeat offenders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#e53e3e',
        fontSize: '14px',
      }}>
        <p>Error loading data: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
      }}>
        <p>No repeat offenders found for this period</p>
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      overflowX: 'auto',
      background: 'white',
      borderRadius: '8px',
    }}>
      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse',
        fontSize: '13px',
      }}>
        <thead>
          <tr style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <th style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Rank
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              User ID
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Incidents
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Unique Chats
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Risk Level
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              First Seen
            </th>
            <th style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Last Seen
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((offender, index) => (
            <tr 
              key={`offender-${index}`}
              style={{
                background: index % 2 === 0 ? '#f7fafc' : 'white',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#edf2f7';
                e.currentTarget.style.transform = 'scale(1.01)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = index % 2 === 0 ? '#f7fafc' : 'white';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: offender.rank <= 3 
                    ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                    : '#cbd5e0',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '13px',
                }}>
                  {offender.rank}
                </div>
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                fontWeight: '600',
                color: '#2d3748',
              }}>
                {offender.userId}
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                textAlign: 'center',
              }}>
                <span style={{
                  background: getSeverityColor(offender.incidentCount),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '12px',
                }}>
                  {offender.incidentCount}
                </span>
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                textAlign: 'center',
                color: '#4a5568',
                fontWeight: '500',
              }}>
                {offender.uniqueChats}
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                textAlign: 'center',
              }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  background: `${getSeverityColor(offender.incidentCount)}20`,
                  color: getSeverityColor(offender.incidentCount),
                  border: `1px solid ${getSeverityColor(offender.incidentCount)}40`,
                }}>
                  {getSeverityBadge(offender.incidentCount)}
                </span>
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                color: '#718096',
                fontSize: '12px',
              }}>
                {offender.firstSeen}
              </td>
              <td style={{
                padding: '14px 16px',
                borderBottom: '1px solid #e2e8f0',
                color: '#718096',
                fontSize: '12px',
              }}>
                {offender.lastSeen}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RepeatOffendersList;